import React, { useState } from 'react';
import { AnalysisResponse } from '../types/analysis';
import HighlightedText from './HighlightedText';

interface ResultsDisplayProps {
  result: AnalysisResponse;
  originalText: string;
  onClose?: () => void;
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ result, originalText, onClose }) => {
  const [expandedTactics, setExpandedTactics] = useState<Set<string>>(new Set());
  const [trustExpanded, setTrustExpanded] = useState(false);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'green':
        return { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800', badge: 'bg-green-500' };
      case 'yellow':
        return { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', badge: 'bg-yellow-500' };
      case 'orange':
        return { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', badge: 'bg-orange-500' };
      case 'red':
        return { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', badge: 'bg-red-500' };
      default:
        return { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-800', badge: 'bg-gray-500' };
    }
  };

  const colors = getSeverityColor(result.severity_level);

  const getRobotFace = (severity: string) => {
    const c = { red: '#ef4444', orange: '#f97316', yellow: '#eab308', green: '#22c55e' }[severity] ?? '#94a3b8';
    return (
      <svg viewBox="0 0 48 58" fill="none" className="w-14 flex-shrink-0">
        {/* Antenna */}
        <line x1="24" y1="0" x2="24" y2="9" stroke={c} strokeWidth="2"/>
        <circle cx="24" cy="3.5" r="3" fill={c}/>
        {/* Head */}
        <rect x="3" y="9" width="42" height="42" rx="7" fill="none" stroke={c} strokeWidth="1.5"/>
        {/* Rivet dots */}
        <circle cx="8"  cy="14" r="1.5" fill={c} fillOpacity="0.4"/>
        <circle cx="40" cy="14" r="1.5" fill={c} fillOpacity="0.4"/>

        {severity === 'red' && <>
          {/* X eyes */}
          <line x1="13" y1="23" x2="20" y2="30" stroke={c} strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="20" y1="23" x2="13" y2="30" stroke={c} strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="28" y1="23" x2="35" y2="30" stroke={c} strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="35" y1="23" x2="28" y2="30" stroke={c} strokeWidth="2.5" strokeLinecap="round"/>
          {/* Frown */}
          <path d="M13 41 Q24 34 35 41" stroke={c} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        </>}

        {severity === 'orange' && <>
          {/* Circle eyes + angry brows */}
          <circle cx="16" cy="26" r="5" fill="none" stroke={c} strokeWidth="1.5"/>
          <circle cx="32" cy="26" r="5" fill="none" stroke={c} strokeWidth="1.5"/>
          <circle cx="16" cy="28" r="2" fill={c}/>
          <circle cx="32" cy="28" r="2" fill={c}/>
          <line x1="11" y1="19" x2="21" y2="21" stroke={c} strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="27" y1="21" x2="37" y2="19" stroke={c} strokeWidth="1.5" strokeLinecap="round"/>
          {/* Frown */}
          <path d="M14 40 Q24 35 34 40" stroke={c} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        </>}

        {severity === 'yellow' && <>
          {/* Rectangular uncertain eyes */}
          <rect x="11" y="22" width="10" height="8" rx="2" fill="none" stroke={c} strokeWidth="1.5"/>
          <rect x="27" y="22" width="10" height="8" rx="2" fill="none" stroke={c} strokeWidth="1.5"/>
          <rect x="14" y="24.5" width="4" height="3" rx="1" fill={c}/>
          <rect x="30" y="24.5" width="4" height="3" rx="1" fill={c}/>
          {/* Wavy mouth */}
          <path d="M13 38 L18 40 L24 37 L30 40 L35 38" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </>}

        {severity === 'green' && <>
          {/* Happy arc eyes */}
          <path d="M11 28 Q16 22 21 28" stroke={c} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
          <path d="M27 28 Q32 22 37 28" stroke={c} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
          {/* Big smile */}
          <path d="M13 36 Q24 46 35 36" stroke={c} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        </>}
      </svg>
    );
  };

  const getTacticSeverityColor = (severity: number) => {
    if (severity >= 8) return 'text-red-600 font-bold';
    if (severity >= 6) return 'text-orange-600 font-semibold';
    if (severity >= 4) return 'text-yellow-600';
    return 'text-green-600';
  };

  const groupedTactics = result.tactics_detected.reduce((acc, tactic) => {
    if (!acc[tactic.tactic_name]) {
      acc[tactic.tactic_name] = [];
    }
    acc[tactic.tactic_name].push(tactic);
    return acc;
  }, {} as Record<string, typeof result.tactics_detected>);

  const toggleTactic = (tacticName: string) => {
    setExpandedTactics(prev => {
      const next = new Set(prev);
      if (next.has(tacticName)) {
        next.delete(tacticName);
      } else {
        next.add(tacticName);
      }
      return next;
    });
  };

  return (
    <div className="mt-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Analysis Results</h2>
        <span className="text-sm text-gray-500">
          {new Date(result.analysis_metadata.timestamp).toLocaleString()}
        </span>
      </div>

      {/* 1. Analyzed prompt with highlights — top so user sees what was analyzed */}
      <HighlightedText
        originalText={originalText}
        segments={result.highlighted_segments}
      />

      {/* 2. Trust Recommendation — compact header, expandable details */}
      <div
        className={`rounded-lg border-2 p-4 cursor-pointer select-none ${
          result.trust_recommendation.should_trust
            ? 'bg-green-50 border-green-300'
            : 'bg-red-50 border-red-300'
        }`}
        onClick={() => setTrustExpanded(!trustExpanded)}
      >
        <div className="flex items-center gap-3">
          <div
            className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
              result.trust_recommendation.should_trust ? 'bg-green-500' : 'bg-red-500'
            }`}
          >
            {result.trust_recommendation.should_trust ? (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <div className="flex-1 flex items-center gap-3 flex-wrap">
            <h3 className="font-bold text-gray-900">Trust Recommendation</h3>
            <span
              className={`inline-block px-3 py-0.5 rounded-full text-sm font-bold ${
                result.trust_recommendation.should_trust
                  ? 'bg-green-200 text-green-900'
                  : 'bg-red-200 text-red-900'
              }`}
            >
              {result.trust_recommendation.should_trust ? 'Can Trust' : 'Do Not Trust'}
            </span>
            <span className="text-sm text-gray-600">
              Confidence: <span className="font-semibold">{result.trust_recommendation.confidence}</span>
            </span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
              trustExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {trustExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
            <p className="text-gray-800 text-sm">
              <span className="font-semibold">Reasoning:</span>{' '}
              {result.trust_recommendation.reasoning}
            </p>
            <p className="text-gray-700 text-sm">
              <span className="font-semibold">Suggested Action:</span>{' '}
              {result.trust_recommendation.suggested_action}
            </p>
          </div>
        )}
      </div>

      {/* 3. Score Card */}
      <div className={`${colors.bg} ${colors.border} border-2 rounded-lg p-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Manipulation Score</h3>
            <div className="flex items-baseline gap-2">
              <span className={`text-6xl font-bold ${colors.text}`}>{result.overall_score}</span>
              <span className="text-3xl text-gray-600">/10</span>
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
            {getRobotFace(result.severity_level)}
            <div
              className={`px-4 py-2 rounded-full ${colors.badge} text-white font-bold text-lg`}
            >
              {result.severity_level.toUpperCase()}
            </div>
            <p className="text-sm text-gray-600 text-center">
              {result.tactics_detected.length} tactic
              {result.tactics_detected.length !== 1 ? 's' : ''} detected
            </p>
          </div>
        </div>
      </div>

      {/* 4. Tactics — compact rows, individually expandable */}
      {result.tactics_detected.length > 0 && (
        <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Manipulation Tactics Detected</h3>
          <div className="space-y-2">
            {Object.entries(groupedTactics).map(([tacticName, instances]) => {
              const isExpanded = expandedTactics.has(tacticName);
              const maxSeverity = Math.max(...instances.map(i => i.severity));
              return (
                <div key={tacticName} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Compact header — always visible */}
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    onClick={() => toggleTactic(tacticName)}
                  >
                    <div className="flex-1 flex items-center gap-3 flex-wrap min-w-0">
                      <span className="font-semibold text-gray-900">{tacticName}</span>
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full flex-shrink-0">
                        {instances[0].category}
                      </span>
                    </div>
                    <span
                      className={`text-lg font-bold flex-shrink-0 ${getTacticSeverityColor(maxSeverity)}`}
                    >
                      {maxSeverity}/10
                    </span>
                    <svg
                      className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 px-4 py-3 space-y-3 bg-gray-50">
                      {instances.map((tactic, index) => (
                        <div key={index}>
                          {instances.length > 1 && (
                            <p className="text-xs font-semibold text-gray-500 mb-1">
                              Instance {index + 1}
                            </p>
                          )}
                          <p className="text-gray-700 text-sm mb-1.5">
                            <span className="font-semibold">Why this is manipulative:</span>{' '}
                            {tactic.explanation}
                          </p>
                          <p className="text-gray-600 text-sm">
                            <span className="font-semibold text-green-700">Authentic alternative:</span>{' '}
                            {tactic.authentic_alternative}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 5. Metadata */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex flex-wrap gap-4">
          <div>
            <span className="font-semibold">Version:</span> API prototype (v1)
          </div>
          <div>
            <span className="font-semibold">Processing Time:</span>{' '}
            {(result.analysis_metadata.processing_time_ms / 1000).toFixed(2)}s
          </div>
        </div>
      </div>

      {onClose && (
        <div className="flex justify-center pt-2 pb-1">
          <button
            onClick={onClose}
            className="text-sm text-gray-400 hover:text-gray-700 font-semibold px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            × Close results
          </button>
        </div>
      )}
    </div>
  );
};

export default ResultsDisplay;
